/**
 * Centralized rate-limit (429) / server-error (5xx) backoff for the shared API client.
 *
 * A module-level gate is keyed by request *origin* (host) because rate limits are enforced
 * per-host on the backend. When any request to a host is rate-limited, subsequent requests to
 * that host wait out the backoff instead of piling on — which is what turns a per-encoder poll
 * burst into a 429 storm. Pairs with the per-tick concurrency cap (ENCODER_POLL_CONCURRENCY).
 */

const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 30_000;
const JITTER_RATIO = 0.25;
const FALLBACK_ORIGIN = 'http://localhost';
const MIN_RETRY_AFTER_MS = 1_000;

type GateEntry = { consecutive: number; until: number };

const rateLimitGates = new Map<string, GateEntry>();

const getBaseOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return FALLBACK_ORIGIN;
};

const originKey = (url: string): string => {
  try {
    // Resolve relatives against current origin so host-level gates apply consistently.
    return new URL(url, getBaseOrigin()).origin;
  } catch {
    return getBaseOrigin();
  }
};

/** Parse a `Retry-After` header (delta-seconds or HTTP-date) into ms from now. */
export const parseRetryAfter = (header?: string | number | null): number | undefined => {
  if (header == null || header === '') return undefined;
  const seconds = Number(header);
  if (!Number.isNaN(seconds)) return Math.max(0, seconds * 1_000);
  const dateMs = Date.parse(String(header));
  if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
  return undefined;
};

/**
 * Exponential backoff with +/- jitter: 2s, 4s, 8s, 16s, 30s (capped).
 * A parsed `Retry-After` value, when present, is authoritative and is returned as-is.
 */
export const getRetryDelayMs = (consecutive: number, retryAfterMs?: number): number => {
  if (retryAfterMs != null) return Math.max(MIN_RETRY_AFTER_MS, retryAfterMs);
  const exp = Math.min(BASE_DELAY_MS * 2 ** Math.max(0, consecutive - 1), MAX_DELAY_MS);
  const jitter = exp * JITTER_RATIO * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(exp + jitter));
};

/** ms remaining before the host's gate clears, or 0 if not currently rate-limited. */
export const getRateLimitWaitMs = (url: string): number => {
  const entry = rateLimitGates.get(originKey(url));
  if (!entry) return 0;
  return Math.max(0, entry.until - Date.now());
};

/**
 * Record a rate-limit / server-error hit for the request's host and open (or extend) its gate.
 * Returns the computed backoff and the running consecutive count (1 == just entered backoff).
 */
export const registerRateLimit = (
  url: string,
  retryAfterHeader?: string | number | null
): { backoffMs: number; consecutive: number } => {
  const key = originKey(url);
  const existing = rateLimitGates.get(key);
  const isActive = (existing?.until ?? 0) > Date.now();
  const consecutive = isActive ? (existing?.consecutive ?? 0) + 1 : 1;
  const backoffMs = getRetryDelayMs(consecutive, parseRetryAfter(retryAfterHeader));
  rateLimitGates.set(key, { consecutive, until: Date.now() + backoffMs });
  return { backoffMs, consecutive };
};

/** Clear a host's gate only when it has naturally expired. */
export const clearRateLimitIfExpired = (url: string): boolean => {
  const key = originKey(url);
  const entry = rateLimitGates.get(key);
  if (!entry || entry.until <= Date.now()) {
    rateLimitGates.delete(key);
    return true;
  }
  return false;
};

/** Resolve after `ms`, rejecting early if the provided signal aborts. */
export const waitWithSignal = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

/** Test-only: reset all gates between cases. */
export const __resetRateLimitGates = (): void => rateLimitGates.clear();
